import type { TFunction } from "i18next";
import { artSlotFields, artSlotNumbers, artSlotStyles } from "../../../lib/schedule-presets/art-fields";

export function metadataLabels(t: TFunction): Record<string, string> {
  const slotLabels = artSlotNumbers.flatMap(number =>
    artSlotFields.map(field => [`slot${number}.${field.key}`, t(field.labelKey, { number })]),
  );
  const styleLabels = artSlotStyles.map(style => [style.key, t(style.labelKey)]);
  const days = {
    mon: t("metadata.field.mon"), tue: t("metadata.field.tue"), wed: t("metadata.field.wed"),
    thu: t("metadata.field.thu"), fri: t("metadata.field.fri"), sat: t("metadata.field.sat"), sun: t("metadata.field.sun"),
  };
  const offlineStyles = Object.entries(days).map(([key, day]) => [
    `builder_offline_${key}_0`, t("builder.offline.dayStyle", { day }),
  ]);
  const streamModes = ["one_slot", "two_slot"] as const;
  const streamStyles = streamModes.flatMap(mode => artSlotNumbers.flatMap(number => {
    if (mode === "one_slot" && number === 2) return [];
    return artSlotFields.flatMap(field => {
      const label = t(field.labelKey, { number });
      const shared = [`builder_${mode}_slot${number}_${field.key}`, label];
      const custom = Object.entries(days).map(([key, day]) => [
        `builder_${mode}_${key}_slot${number}_${field.key}`, `${day} · ${label}`,
      ]);
      return [shared, ...custom];
    });
  }));
  const streamExceptions = streamModes.flatMap(mode => Object.entries(days).flatMap(([key, day]) => {
    const count = mode === "one_slot" ? artSlotFields.length : artSlotFields.length * 2;
    return Array.from({ length: count }, (_, index) => [
      `builder_${mode}_${key}_${index}`, t(mode === "one_slot" ? "builder.stream.dayStyle" : "builder.twoStreams.dayStyle", { day, number: index + 1 }),
    ]);
  }));
  return {
    ...Object.fromEntries(slotLabels),
    ...Object.fromEntries(styleLabels),
    ...Object.fromEntries(offlineStyles),
    ...Object.fromEntries(streamStyles),
    ...Object.fromEntries(streamExceptions),
    ...days,
    "profile.artist_handle": t("art.creatorHandle"),
    "week.range": t("metadata.field.weekRange"),
    "week.start_date": t("metadata.binding.weekStart"),
    "week.end_date": t("metadata.binding.weekEnd"),
    "offline.message": t("schedule.offlineMessage"),
    icon: t("metadata.icon"),
    black_icon: t("metadata.field.blackIcon"),
    styles: t("metadata.field.styles"),
    formats: t("metadata.field.formats"),
    globals: t("metadata.field.globals"),
    days: t("metadata.field.days"),
    art_creator: t("metadata.field.artCreator"),
    week_range: t("metadata.field.weekRange"),
    builder_week_dates: t("builder.dates.dateStyle"),
    builder_week_month_text: t("builder.dates.monthStyle"),
    builder_week_day_text: t("builder.dates.dayStyle"),
    week_start_date: t("metadata.field.weekStartDate"),
    week_end_date: t("metadata.field.weekEndDate"),
    offline_label: t("metadata.field.offlineLabel"),
    builder_offline_text: t("builder.offline.sharedStyle"),
    builder_day_date_text: t("builder.stream.dayDate"),
    week_date: t("metadata.field.weekDate"),
    clock: t("metadata.field.clock"),
    origin: t("metadata.field.origin"),
    offline: t("metadata.field.offline"),
    one_slot: t("metadata.field.oneSlot"),
    two_slot: t("metadata.field.twoSlot"),
    date: t("metadata.field.date"),
    font: t("metadata.field.font"),
    size: t("metadata.field.size"),
    color: t("metadata.field.color"),
    stroke: t("metadata.field.stroke"),
    center: t("metadata.stroke.center"),
    outside: t("metadata.stroke.outside"),
    width: t("metadata.field.width"),
    weight: t("metadata.field.weight"),
    capitalize: t("metadata.field.capitalize"),
    align: t("metadata.field.align"),
    left: t("metadata.align.left"),
    right: t("metadata.align.right"),
    bind: t("metadata.field.bind"),
    style: t("metadata.field.style"),
    format: t("metadata.field.format"),
    position: t("metadata.field.position"),
    rotation: t("metadata.field.rotation"),
    type: t("metadata.field.type"),
    offset: t("metadata.field.offset"),
    scale: t("metadata.field.scale"),
  };
}
